import { Injectable } from '@angular/core';
import { DestinationType, HotelCriteria, TourCriteria } from '@surf/surf-components-core';
import { TC } from '@tc-core/util';
import { ConfigLoader } from '@tc-core/util/framework/config-loader.service';

@Injectable({
  providedIn: 'root',
})
export class HelperService {
  HTL_SEARCH_EXPAND_RESULT = 'true';
  HTL_SEARCH_ENABLE_SOURCE_SUMMARY_CACHE = 'true';
  FIELD_SEPARATOR = '~';
  SEARCH_SOURCES = 'tbx~1~4';
  ENABLE_FILTERED_RESULTS = true;
  DEFAULT_ADULT_AGE = 30;

  constructor(private configLoader: ConfigLoader) {
    this.setupModuleConfigs();
  }

  /**
   * Sets up module configurations.
   * @returns void
   */
  setupModuleConfigs(): void {
    this.HTL_SEARCH_EXPAND_RESULT = this.configLoader.getModuleConfig(
      'HTL_SEARCH_EXPAND_RESULT',
      TC.MODULE_NAME.SURF_B2B
    );
    this.HTL_SEARCH_ENABLE_SOURCE_SUMMARY_CACHE =
      this.configLoader.getModuleConfig(
        'HTL_SEARCH_ENABLE_SOURCE_SUMMARY_CACHE',
        TC.MODULE_NAME.SURF_B2B
      );
    this.SEARCH_SOURCES = this.configLoader.getModuleConfig(
      'SEARCH_SOURCES',
      TC.MODULE_NAME.SURF_B2B
    );
    this.DEFAULT_ADULT_AGE = +this.configLoader.getModuleConfig(
      'DEFAULT_ADULT_AGE',
      TC.MODULE_NAME.SURF_B2B
    );
  }

  /**
   * Converts the response to a recommendation list.
   * @param response The response to convert.
   * @param keyParams The base criteria.
   * @returns The recommendation list.
   * @throws An error if something goes wrong while constructing the promo recommendation list.
   */
  convertResponseToRecommendationList(response: any, keyParams): any {
    const recommendationList = [];

    try {
      response.forEach((item: any) => {
        const attributeMap: { [key: string]: string } = {};
        for (const attribute of item.detail.attributes) {
          attributeMap[attribute.name] = attribute.value;
        }
        if (item.code === 'HTL') {
          const hotelItem = {
            heading: item.name,
            description: attributeMap.DESCRIPTION,
            descLink: this.constructHotelSearchCriteria(
              attributeMap,
              keyParams
            ),
            imageURL: attributeMap.IMAGE_URL,
          };
          recommendationList.push(hotelItem);
        } else if (item.code === 'TOU') {
          const tourItem = {
            heading: item.name,
            description: attributeMap.DESCRIPTION,
            descLink: this.constructTourSearchCriteria(
              attributeMap,
              keyParams
            ),
            imageURL: attributeMap.IMAGE_URL,
          };
          recommendationList.push(tourItem);
        }
      });
    } catch (error) {
      throw new Error(
        'Something went wrong while constructing promo recommendation list'
      );
    }

    return recommendationList;
  }

  /**
   * Returns a string representing the start date based on the current date and the number of months to add.
   * @param months - The number of months to add to the current date.
   * @param appendISOLikeTimeString - Whether to append an ISO-like time string to the date.
   * @returns A string representing the start date in the format 'YYYY-MM-DD' (optionally with 'T00:00:00' appended).
   */
  getStartDate(months: string | number, appendISOLikeTimeString: boolean = false): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    const day = today.getDate();
    // Parse months as a number
    const parsedMonths = Number(months);
    // Calculate the future year and month, considering overflow
    const futureYear = year + Math.floor((month + parsedMonths - 1) / 12);
    const futureMonth = ((month + parsedMonths - 1) % 12) + 1;
    const futureDate = new Date(futureYear, futureMonth - 1, day);
    const futureMonthStr = this.pad(futureDate.getMonth() + 1, 2);
    const futureDayStr = this.pad(futureDate.getDate(), 2);
    return futureYear + '-' + futureMonthStr + '-' + futureDayStr + (appendISOLikeTimeString ? 'T00:00:00' : '');
  }

  /**
   * Pads a number with leading zeros until it reaches the specified size.
   * @param num The number to pad.
   * @param size The desired size of the padded number.
   * @returns The padded number as a string.
   */
  pad(num: string | number, size: number): string {
    let s = num.toString();
    while (s.length < size) {
      s = '0' + s;
    }
    return s;
  }

  /**
   * Constructs the search criteria for hotels based on the given attribute map and key parameters.
   * @param attributeMap - The map of attributes to build the hotel criteria from.
   * @param keyParams - The key parameters to include in the search criteria.
   * @returns The constructed search criteria for hotels.
   */
  constructHotelSearchCriteria(attributeMap: any, keyParams): string {
    const basePath = '/b2b-hotels/';
    const hotelCriteria = this.buildHotelCriteria(attributeMap);
    const queryStr = this.buildHotelQueryParams(hotelCriteria);
    return basePath + '?' + keyParams + queryStr;
  }

  /**
   * Builds a HotelCriteria object based on the given attribute map.
   * @param attributeMap - The attribute map to build the HotelCriteria object from.
   * @returns A HotelCriteria object.
   */
  buildHotelCriteria(attributeMap: any): HotelCriteria {
    const hotelCriteria = new HotelCriteria();

    // adult: 2~30~~1 adult count ~ age of adults ~~ number of rooms
    hotelCriteria.adult = `${attributeMap.ADULT}${this.FIELD_SEPARATOR}${this.DEFAULT_ADULT_AGE}~~${attributeMap.NUMBER_OF_ROOM}`;
    hotelCriteria.child = attributeMap.CHILDREN;
    hotelCriteria.infant = attributeMap.INFANT;
    hotelCriteria.startDate = this.getStartDate(attributeMap.DEPART_IN_MONTHS, true);
    hotelCriteria.nights = attributeMap.NUMBER_OF_NIGHTS;
    hotelCriteria.destinationCode = attributeMap.DESTINATION_CITY;
    hotelCriteria.destinationName = attributeMap.HOTEL_NAME;
    hotelCriteria.destinationParentCode = attributeMap.DESTINATION_PARENT;
    hotelCriteria.destinationType = DestinationType.HOTEL;

    // Remove child and infant if they are 0
    for (const key in hotelCriteria) {
      if (
        hotelCriteria.hasOwnProperty(key) &&
        (key === 'child' || key === 'infant')
      ) {
        if (Number(hotelCriteria[key]) === 0) {
          hotelCriteria[key] = undefined;
        } else {
          if (key === 'child') {
            hotelCriteria[key] = `${attributeMap.CHILDREN}${this.FIELD_SEPARATOR}${attributeMap.CHILDREN_AGE}~~` +
              attributeMap.NUMBER_OF_ROOM;
          } else if (key === 'infant') {
            hotelCriteria[key] = `${attributeMap.INFANT}${this.FIELD_SEPARATOR}${attributeMap.INFANT_AGE}~~` +
              attributeMap.NUMBER_OF_ROOM;
          }
         }
      }
    }

    return hotelCriteria;
  }

  /**
   * Builds the query parameters for a hotel search based on the given criteria.
   * @param hotelCriteria - The criteria for the hotel search.
   * @returns The query parameters for the hotel search.
   */
  buildHotelQueryParams(hotelCriteria: HotelCriteria): string {
    const {
      teen,
      child,
      infant,
      adult,
      startDate,
      nights,
      destinationCode,
      destinationName,
      destinationParentCode,
      destinationType,
    } = hotelCriteria;

    // Generate the child parameter by combining the teen, child, and infant parameters
    const childParams = [
      teen && `&child=${teen}`,
      child && `&child=${child}`,
      infant && `&infant=${infant}`
    ].filter(Boolean).join(this.FIELD_SEPARATOR);

    // Generate the query string for the hotel search
    let queryStr = [
      `&adult=${adult}`,
      childParams,
      `&startDate=${startDate}`,
      `&nights=${nights}`,
      destinationCode && `&destinationCode=${destinationCode}`,
      destinationType && `&destinationType=${destinationType}`,
      `&hotelName=${destinationName}`,
      `&dn=${destinationName}`,
      '&isRoomWise=true',
      destinationParentCode && `&dp=${destinationParentCode}`,
      '&expand=' + this.HTL_SEARCH_EXPAND_RESULT,
      `&enableSourceSummaryCache=${this.HTL_SEARCH_ENABLE_SOURCE_SUMMARY_CACHE}`,
      this.SEARCH_SOURCES && `&sources=${this.SEARCH_SOURCES}`,
    ].filter(Boolean).join('');

    // Enable the filtered results attribute in the query string
    if (this.ENABLE_FILTERED_RESULTS && queryStr.includes('&attributes=')) {
      queryStr = queryStr.replace(/(&attributes=[^&]*)/, '$1,ENABLE_FILTERED_RESULTS~true');
    } else if (this.ENABLE_FILTERED_RESULTS) {
      queryStr += '&attributes=ENABLE_FILTERED_RESULTS~true';
    }

    return queryStr;
  }

  /**
   * Constructs a search criteria for a tour based on the given attribute map and key parameters.
   * @param attributeMap - The map of attributes to use in the search criteria.
   * @param keyParams - The key parameters to include in the search criteria.
   * @returns A string representing the search criteria for the tour.
   */
  constructTourSearchCriteria(attributeMap: any, keyParams: any): string {
    const basePath = '/b2b-tours/';
    const tourCriteria = this.buildTourCriteria(attributeMap);
    const queryStr = this.buildTourQueryParams(tourCriteria);
    return basePath + '?' + keyParams + queryStr;
  }

  /**
   * Builds tour criteria object based on the given attribute map.
   * @param attributeMap - The attribute map to build the tour criteria from.
   * @returns The tour criteria object.
   */
  buildTourCriteria(attributeMap: any): any {
    const tourCriteria = new TourCriteria();

    tourCriteria.adult = `${attributeMap.ADULT}${this.FIELD_SEPARATOR}${this.DEFAULT_ADULT_AGE}${this.FIELD_SEPARATOR}`;
    tourCriteria.child = attributeMap.CHILDREN;
    tourCriteria.infant = attributeMap.INFANT;
    tourCriteria.startDate = this.getStartDate(attributeMap.DEPART_IN_MONTHS);
    tourCriteria.destinationCode = attributeMap.DESTINATION_CITY;
    tourCriteria.city = attributeMap.DESTINATION_CITY;
    tourCriteria.destinationName = attributeMap.DESTINATION_PARENT;
    tourCriteria.durationType = attributeMap.DURATION_TYPE;
    tourCriteria.itineraryName = attributeMap.ITINERARY_NAME;

    // Remove child and infant if they are 0
    for (const key in tourCriteria) {
      if (
        tourCriteria.hasOwnProperty(key) &&
        (key === 'child' || key === 'infant')
      ) {
        if (Number(tourCriteria[key]) === 0) {
          tourCriteria[key] = undefined;
        } else {
          if (key === 'child') {
            tourCriteria[key] = `${attributeMap.CHILDREN}${this.FIELD_SEPARATOR}${attributeMap.CHILDREN_AGE}${this.FIELD_SEPARATOR}`;
          } else if (key === 'infant') {
            tourCriteria[key] = `${attributeMap.INFANT}${this.FIELD_SEPARATOR}${attributeMap.INFANT_AGE}${this.FIELD_SEPARATOR}`;
          }
        }
      }
    }

    return tourCriteria;
  }

  /**
   * Builds query parameters for a tour based on the given criteria.
   * @param tourCriteria - The criteria for the tour.
   * @returns A string representing the query parameters for the tour.
   */
  buildTourQueryParams(tourCriteria: TourCriteria): string {
    const {
      teen,
      child,
      infant,
      adult,
      startDate,
      city,
      destinationName,
      durationType,
      itineraryName,
      destinationParentCode,
    } = tourCriteria;

    // Generate the child parameter by combining the teen, child, and infant parameters
    const childParams = [
      teen && `&child=${teen}`,
      child && `&child=${child}`,
      infant && `&infant=${infant}`
    ].filter(Boolean).join(this.FIELD_SEPARATOR);

    const queryStr = [
      `&adult=${adult}`,
      childParams,
      city && `&city=${city}`,
      `&startDate=${startDate}`,
      `&durationType=${durationType === 'A' ? 'ANY' : durationType}`,
      itineraryName && `&itineraryName=${encodeURIComponent(itineraryName)}`,
      destinationName && `&destinationName=${destinationName}`,
      destinationParentCode && `&destinationParentCode=${destinationParentCode}`,
      '&expand=all',
    ].filter(Boolean).join('');

    return queryStr;
  }
}
